import {
  CSSProperties,
  ReactElement,
  RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import {createPortal} from 'react-dom'
import classNames from 'classnames'
import {useTranslation} from 'react-i18next'
import {useLocation} from 'react-router-dom'

import {PlusIconCorrect, TooltipWrapper} from 'components'
import {KEY_CODES} from 'definitions/constant'
import {useWindowSize} from 'hooks'
import {DopamineAssistantCollapseIcon} from './DopamineAssistantCollapseIcon'
import {DopamineAssistantConversation} from './DopamineAssistantConversation'
import {DopamineAssistantFullscreenIcon} from './DopamineAssistantFullscreenIcon'
import {
  DopamineAssistantHistory,
  DopamineAssistantHistorySidebar,
} from './DopamineAssistantHistory'
import {DopamineAssistantMark} from './DopamineAssistantMark'
import {DopamineAssistantResizeHandle} from './DopamineAssistantResizeHandle'
import {
  DOPAMINE_HISTORY_SIDEBAR_MIN_VIEWPORT_WIDTH,
  DOPAMINE_PANEL_RESIZE_MIN_VIEWPORT_WIDTH,
  DopamineAssistantStorageScope,
  isDopamineShortcut,
  readDopamineAssistantOpenState,
  subscribeToDopamineAssistantCollapse,
  writeDopamineAssistantOpenState,
} from './DopamineAssistant.utils'
import {DopamineRunOutcome, DopamineRunState} from './DopamineAssistant.types'
import {useDopamineAssistantSession} from './useDopamineAssistantSession'
import {useDopamineHistorySidebarWidth} from './useDopamineHistorySidebarWidth'
import {useDopaminePanelWidth} from './useDopaminePanelWidth'
import './DopamineAssistant.css'

const PANEL_ID = 'dopamine-assistant-panel'
const RAIL_STATUS_ID = 'dopamine-assistant-rail-status'

type DopamineRailStatus =
  | 'idle'
  | 'running'
  | 'unavailable'
  | DopamineRunOutcome

const shouldRenderHistorySidebar = (
  isFullscreen: boolean,
  viewportWidth: number,
): boolean =>
  isFullscreen && viewportWidth >= DOPAMINE_HISTORY_SIDEBAR_MIN_VIEWPORT_WIDTH

const getPanelSizing = (
  isExpanded: boolean,
  isFullscreen: boolean,
  viewportWidth: number,
  width: number | null,
): {canResizePanel: boolean; panelWidthStyle: CSSProperties | undefined} => {
  // The panel edge is draggable where the panel is not already viewport-wide.
  const canResizePanel =
    isExpanded &&
    !isFullscreen &&
    viewportWidth >= DOPAMINE_PANEL_RESIZE_MIN_VIEWPORT_WIDTH

  return {
    canResizePanel,
    panelWidthStyle: canResizePanel && width !== null ? {width} : undefined,
  }
}

interface DopamineAssistantShellProps {
  className?: string
  isVisible: boolean
  storageScope: DopamineAssistantStorageScope | null
}

interface DopamineAssistantRailProps {
  buttonRef: RefObject<HTMLButtonElement>
  hasUnreadOutcome: boolean
  isExpanded: boolean
  onOpen: () => void
  railStatus: DopamineRailStatus
  railStatusText: string | null
}

const DopamineAssistantRail = ({
  buttonRef,
  hasUnreadOutcome,
  isExpanded,
  onOpen,
  railStatus,
  railStatusText,
}: DopamineAssistantRailProps): ReactElement => {
  const {t} = useTranslation(['common'], {useSuspense: false})
  const [isHovered, setIsHovered] = useState(false)
  return (
    <>
      <button
        aria-controls={PANEL_ID}
        aria-describedby={railStatusText ? RAIL_STATUS_ID : undefined}
        aria-expanded="false"
        aria-label={t('OPEN_DOPAMINE_ASSISTANT')}
        className={classNames(
          // Slightly raise the rail icon to align with the adjacent page title.
          'dopamine-rail-surface relative h-full w-12 flex-col items-center pt-4',
          isExpanded ? 'hidden' : 'flex',
        )}
        data-cy="dopamine-assistant-open"
        data-status={railStatus}
        hidden={isExpanded}
        ref={buttonRef}
        type="button"
        onClick={() => {
          setIsHovered(false)
          onOpen()
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <span
          aria-hidden="true"
          className="absolute right-0 top-0 h-full w-px bg-gradient-to-b from-white/20 via-white/10 to-transparent"
        />
        <span
          className={classNames(
            'dopamine-rail-button',
            railStatus === 'unavailable' && 'opacity-40',
          )}
          id="dopamine-assistant-rail-icon"
        >
          <DopamineAssistantMark
            animated={railStatus === 'running'}
            className="relative h-4.5 w-4.5"
            gradient
          />
          {hasUnreadOutcome ? (
            <span
              aria-hidden="true"
              className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-dopamine-accent ring-2 ring-dopamine-header"
              data-cy="dopamine-assistant-rail-dot"
            />
          ) : null}
        </span>
        {railStatusText ? (
          <span className="sr-only" id={RAIL_STATUS_ID}>
            {railStatusText}
          </span>
        ) : null}
      </button>
      {!isExpanded &&
        isHovered &&
        createPortal(
          <TooltipWrapper
            anchorSelect="#dopamine-assistant-rail-icon"
            className="karla z-tooltip !rounded-md border-0.5 border-white/10 !bg-[#17171b] !px-2 !py-1 !text-11px !leading-normal !tracking-normal !text-[#f4f4f6] shadow-[0_6px_18px_rgb(0_0_0/0.5)]"
            closeEvents={{}}
            id="dopamine-assistant-rail-tooltip"
            isOpen
            openEvents={{}}
            place="right"
            positionStrategy="fixed"
            variant="dark"
          >
            {t('OPEN_DOPAMINE_ASSISTANT')}
          </TooltipWrapper>,
          document.body,
        )}
    </>
  )
}

export const DopamineAssistantShell = ({
  className,
  isVisible,
  storageScope,
}: DopamineAssistantShellProps): ReactElement => {
  const {t} = useTranslation(['common'], {useSuspense: false})
  const {pathname} = useLocation()
  const {width: viewportWidth} = useWindowSize()
  // The panel reopens as it was left, per account, across Analytics visits.
  const [isExpanded, setIsExpanded] = useState(() =>
    readDopamineAssistantOpenState(storageScope),
  )
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [isHistoryOpen, setIsHistoryOpen] = useState(false)
  const [isHistorySidebarOpen, setIsHistorySidebarOpen] = useState(true)
  const [isRunActive, setIsRunActive] = useState(false)
  const [unreadOutcome, setUnreadOutcome] = useState<DopamineRunOutcome | null>(
    null,
  )
  const isExpandedRef = useRef(isExpanded)
  const openButtonRef = useRef<HTMLButtonElement>(null)
  const collapseButtonRef = useRef<HTMLButtonElement>(null)
  const fullscreenButtonRef = useRef<HTMLButtonElement>(null)
  const composerRef = useRef<HTMLTextAreaElement>(null)
  const shellRef = useRef<HTMLElement>(null)
  const hasToggledRef = useRef(false)
  const hasToggledFullscreenRef = useRef(false)
  const shouldFocusComposerAfterNewChatRef = useRef(false)
  const previousPathnameRef = useRef(pathname)
  const session = useDopamineAssistantSession({
    enabled: isExpanded && isVisible,
    storageScope,
  })
  const historySidebarResize = useDopamineHistorySidebarWidth({
    storageScope,
    viewportWidth,
  })
  const {endDrag: endHistorySidebarDrag} = historySidebarResize
  const panelResize = useDopaminePanelWidth({storageScope, viewportWidth})
  const {endDrag: endPanelDrag} = panelResize
  const {canResizePanel, panelWidthStyle} = getPanelSizing(
    isExpanded,
    isFullscreen,
    viewportWidth,
    panelResize.width,
  )
  // Past chats become a sidebar only where it fits beside the reading column.
  const shouldShowHistorySidebar = shouldRenderHistorySidebar(
    isFullscreen,
    viewportWidth,
  )
  const previousHistoryPresentationRef = useRef({
    isFullscreen,
    shouldShowHistorySidebar,
  })
  const isHistoryControlOpen = shouldShowHistorySidebar
    ? isHistorySidebarOpen
    : isHistoryOpen
  const isHistorySidebarVisible =
    session.isApiAvailable && shouldShowHistorySidebar && isHistorySidebarOpen
  const fullscreenLabel = t(
    isFullscreen ? 'DOPAMINE_EXIT_FULLSCREEN' : 'DOPAMINE_ENTER_FULLSCREEN',
  )

  // The rail carries the run: the mark animates while work is in progress and
  // a dot marks a run that ended while the panel was collapsed. Opening the
  // panel clears it.
  const getRailStatus = (): DopamineRailStatus => {
    if (!session.isApiAvailable) return 'unavailable'
    if (unreadOutcome) return unreadOutcome
    return isRunActive ? 'running' : 'idle'
  }
  const railStatus = getRailStatus()
  const getRailStatusText = (): string | null => {
    if (railStatus === 'running') return t('INVESTIGATING')
    if (railStatus === 'answer') return t('DOPAMINE_ANSWER_READY')
    if (railStatus === 'failed') return t('DOPAMINE_RUN_DID_NOT_FINISH')
    if (railStatus === 'unavailable') return t('DOPAMINE_ASSISTANT_UNAVAILABLE')
    return null
  }
  const railStatusText = getRailStatusText()
  const handleRunStateChange = useCallback((state: DopamineRunState): void => {
    setIsRunActive(state.isRunning)
    if (!state.isRunning && state.outcome && !isExpandedRef.current) {
      setUnreadOutcome(state.outcome)
    }
  }, [])

  useEffect(() => {
    isExpandedRef.current = isExpanded
    if (isExpanded) setUnreadOutcome(null)
  }, [isExpanded])

  const getAssistantLabel = (): string => {
    if (isFullscreen) return t('DOPAMINE_ASSISTANT_FULLSCREEN')
    if (isExpanded) return t('DOPAMINE_ASSISTANT')
    return t('DOPAMINE_ASSISTANT_RAIL')
  }

  // Focus goes to the composer when it can take input, otherwise to the
  // control that makes sense for the transition that just happened.
  const focusComposerOr = (fallback: HTMLElement | null): void => {
    const composer = composerRef.current
    const focusTarget = composer && !composer.disabled ? composer : fallback
    focusTarget?.focus()
  }

  useEffect(() => {
    if (!isVisible || !hasToggledRef.current) return

    if (isExpanded) focusComposerOr(collapseButtonRef.current)
    else openButtonRef.current?.focus()
  }, [isExpanded, isVisible])

  useEffect(() => {
    if (!isVisible || !hasToggledFullscreenRef.current) return

    hasToggledFullscreenRef.current = false
    if (isFullscreen) focusComposerOr(fullscreenButtonRef.current)
    else fullscreenButtonRef.current?.focus()
  }, [isFullscreen, isVisible])

  // Keep the current Past Chats state when a resize switches between the
  // narrow popover and wide sidebar presentations while already full screen.
  useLayoutEffect(() => {
    const previousPresentation = previousHistoryPresentationRef.current
    previousHistoryPresentationRef.current = {
      isFullscreen,
      shouldShowHistorySidebar,
    }

    if (
      !previousPresentation.isFullscreen ||
      !isFullscreen ||
      previousPresentation.shouldShowHistorySidebar === shouldShowHistorySidebar
    ) {
      return
    }

    if (shouldShowHistorySidebar) {
      setIsHistorySidebarOpen(isHistoryOpen)
      setIsHistoryOpen(false)
      return
    }

    setIsHistoryOpen(isHistorySidebarOpen)
  }, [
    isFullscreen,
    isHistoryOpen,
    isHistorySidebarOpen,
    shouldShowHistorySidebar,
  ])

  // The resize handle owns pointer capture, so end an active drag before any
  // state change removes that handle from the page.
  useLayoutEffect(() => {
    if (!isHistorySidebarVisible) endHistorySidebarDrag()
  }, [endHistorySidebarDrag, isHistorySidebarVisible])

  useLayoutEffect(() => {
    if (!canResizePanel) endPanelDrag()
  }, [canResizePanel, endPanelDrag])

  useEffect(() => {
    if (isVisible) return

    // Away from Analytics the panel keeps its open state for the return trip;
    // only the transient layers reset.
    hasToggledRef.current = false
    hasToggledFullscreenRef.current = false
    setIsFullscreen(false)
    setIsHistoryOpen(false)
  }, [isVisible])

  useEffect(() => {
    writeDopamineAssistantOpenState(storageScope, isExpanded)
  }, [isExpanded, storageScope])

  // Reveal the destination report after Analytics navigation while keeping the
  // current Assistant panel and conversation mounted.
  useEffect(() => {
    if (previousPathnameRef.current === pathname) return

    previousPathnameRef.current = pathname
    hasToggledFullscreenRef.current = false
    setIsHistoryOpen(false)
    setIsFullscreen(false)
  }, [pathname])

  useEffect(
    () =>
      subscribeToDopamineAssistantCollapse(() => {
        hasToggledRef.current = true
        setIsHistoryOpen(false)
        setIsFullscreen(false)
        setIsExpanded(false)
      }),
    [],
  )

  const updateExpandedState = useCallback((expanded: boolean): void => {
    hasToggledRef.current = true
    if (!expanded) setIsHistoryOpen(false)
    // The rail always opens the panel; full screen is chosen from there.
    setIsFullscreen(false)
    setIsExpanded(expanded)
  }, [])

  const updateFullscreenState = (fullscreen: boolean): void => {
    hasToggledFullscreenRef.current = true
    setIsHistoryOpen(false)
    setIsFullscreen(fullscreen)
  }

  const openHistory = (): void => {
    if (shouldShowHistorySidebar) setIsHistorySidebarOpen(true)
    else setIsHistoryOpen(true)
  }

  const closeHistory = (): void => {
    if (shouldShowHistorySidebar) setIsHistorySidebarOpen(false)
    else setIsHistoryOpen(false)
  }

  const startNewChat = (): void => {
    setIsHistoryOpen(false)
    shouldFocusComposerAfterNewChatRef.current = true
    session.startNewChat()
  }

  // The composer remounts with the new conversation, so focus it once that
  // commit has happened rather than on an animation frame that may race it.
  useEffect(() => {
    if (!shouldFocusComposerAfterNewChatRef.current) return

    shouldFocusComposerAfterNewChatRef.current = false
    focusComposerOr(collapseButtonRef.current)
  }, [session.conversationRevision])

  // ⌘/ (Ctrl+/ elsewhere) toggles the panel from anywhere on Analytics.
  useEffect(() => {
    if (!isVisible) return

    const handleShortcut = (event: globalThis.KeyboardEvent): void => {
      if (!isDopamineShortcut(event)) return

      event.preventDefault()
      if (event.repeat) return

      updateExpandedState(!isExpanded)
    }

    document.addEventListener('keydown', handleShortcut)
    return () => document.removeEventListener('keydown', handleShortcut)
  }, [isExpanded, isVisible, updateExpandedState])

  useEffect(() => {
    if (!isExpanded) return

    const handleDocumentKeyDown = (event: globalThis.KeyboardEvent): void => {
      if (
        event.key !== KEY_CODES.ESCAPE ||
        !shellRef.current?.contains(document.activeElement)
      ) {
        return
      }

      event.preventDefault()
      if (isHistoryOpen) {
        setIsHistoryOpen(false)
        return
      }

      // Escape walks back one layer at a time: past chats, full screen, then
      // the panel itself.
      if (isFullscreen) {
        hasToggledFullscreenRef.current = true
        setIsFullscreen(false)
        return
      }

      hasToggledRef.current = true
      setIsExpanded(false)
    }

    document.addEventListener('keydown', handleDocumentKeyDown)
    return () => document.removeEventListener('keydown', handleDocumentKeyDown)
  }, [isExpanded, isFullscreen, isHistoryOpen])

  const getWidthClassName = (): string => {
    if (isFullscreen) return 'w-full'
    if (isExpanded) return 'w-dopamine-assistant'
    return 'w-12'
  }

  return (
    <aside
      aria-label={getAssistantLabel()}
      className={classNames(
        'dopamine-assistant-root group/dopamine peer/dopamine karla relative isolate h-full shrink-0 overflow-hidden border-r-0.5 border-white/10 text-white',
        'transition-width ease-dope-out motion-reduce:transition-none',
        getWidthClassName(),
        className,
      )}
      data-cy={
        isExpanded ? 'dopamine-assistant-panel' : 'dopamine-assistant-rail'
      }
      data-expanded={isExpanded}
      data-fullscreen={isFullscreen}
      data-resized={panelWidthStyle !== undefined}
      data-resizing={historySidebarResize.isResizing || panelResize.isResizing}
      hidden={!isVisible}
      id={PANEL_ID}
      ref={shellRef}
      style={panelWidthStyle}
    >
      <div
        className={classNames(
          'relative flex h-full min-w-dopamine-assistant flex-col',
          isExpanded ? 'bg-transparent' : 'bg-black/50 backdrop-blur-xl',
        )}
        style={panelWidthStyle ? {minWidth: panelWidthStyle.width} : undefined}
      >
        <div
          className={classNames(
            'dopamine-expanded-surface min-h-0 flex-1 flex-col',
            isExpanded ? 'flex' : 'hidden',
          )}
          hidden={!isExpanded}
        >
          <span aria-hidden="true" className="dopamine-shell-aurora" />
          <span aria-hidden="true" className="dopamine-shell-grain" />
          {/* In the panel the header drops 12px so its centre meets the page
              title beside it (20px padding + 32px line = 36px on Overview,
              Policy, Productivity and Shadow IT). Full screen has no title
              beside it and keeps the header at the top. */}
          <header className="dopamine-header group-data-[fullscreen=false]/dopamine:mt-3 group-data-[fullscreen=false]/dopamine:!pb-3">
            <div className="dopamine-header-brand">
              <span className="dopamine-mark-chip flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-white shadow-inset-subtle">
                <DopamineAssistantMark className="h-4 w-4" />
              </span>
              <h2
                aria-label={t('DOPAMINE')}
                className="dopamine-wordmark min-w-0 truncate text-sm leading-none text-white/90"
              >
                <span className="abc-bold">DOP</span>
                <span className="cositimes">AMINE</span>
              </h2>
            </div>
            <div className="dopamine-header-actions">
              {session.isApiAvailable ? (
                <button
                  aria-label={t('DOPAMINE_NEW_CHAT')}
                  className="dopamine-header-icon-button dopamine-tip disabled:cursor-default disabled:opacity-40"
                  data-cy="dopamine-assistant-new-chat"
                  data-tip={t('DOPAMINE_NEW_CHAT')}
                  disabled={!session.hasConversation || session.isHistoryError}
                  type="button"
                  onClick={startNewChat}
                >
                  <PlusIconCorrect
                    classes="h-4 w-4"
                    height={16}
                    strokeColor="currentColor"
                    width={16}
                  />
                </button>
              ) : null}
              {session.isApiAvailable ? (
                <DopamineAssistantHistory
                  controls={shouldShowHistorySidebar ? 'sidebar' : 'popover'}
                  isOpen={isHistoryControlOpen}
                  session={session}
                  onClose={closeHistory}
                  onOpen={openHistory}
                />
              ) : null}
              <button
                aria-label={fullscreenLabel}
                aria-pressed={isFullscreen}
                className="dopamine-header-icon-button dopamine-tip dopamine-fullscreen-button group-data-[fullscreen=true]/dopamine:w-auto group-data-[fullscreen=true]/dopamine:gap-1.5 group-data-[fullscreen=true]/dopamine:px-2.5"
                data-cy="dopamine-assistant-fullscreen"
                data-tip={fullscreenLabel}
                ref={fullscreenButtonRef}
                type="button"
                onClick={() => updateFullscreenState(!isFullscreen)}
              >
                <DopamineAssistantFullscreenIcon isFullscreen={isFullscreen} />
                {/* Full screen has room to say what the control does. */}
                <span className="hidden text-xs group-data-[fullscreen=true]/dopamine:inline">
                  {t('DOPAMINE_EXIT_FULLSCREEN')}
                </span>
              </button>
              <button
                aria-controls={PANEL_ID}
                aria-expanded="true"
                aria-label={t('COLLAPSE_DOPAMINE_ASSISTANT')}
                className="dopamine-header-icon-button dopamine-tip dopamine-collapse-button"
                data-tip={t('COLLAPSE_DOPAMINE_ASSISTANT')}
                data-cy="dopamine-assistant-collapse"
                ref={collapseButtonRef}
                type="button"
                onClick={() => updateExpandedState(false)}
              >
                <DopamineAssistantCollapseIcon />
              </button>
            </div>
          </header>
          <div className="flex min-h-0 flex-1">
            {isHistorySidebarVisible ? (
              <DopamineAssistantHistorySidebar
                isNewChatDisabled={
                  !session.hasConversation || session.isHistoryError
                }
                resize={historySidebarResize}
                session={session}
                onNewChat={startNewChat}
              />
            ) : null}
            <div className="flex min-h-0 min-w-0 flex-1 flex-col">
              <div className="flex min-h-0 w-full flex-1 flex-col">
                {session.isApiAvailable ? (
                  <DopamineAssistantConversation
                    composerRef={composerRef}
                    isVisible={isExpanded}
                    onRunStateChange={handleRunStateChange}
                    onStartNewChat={startNewChat}
                    session={session}
                  />
                ) : (
                  <output
                    className="relative flex min-h-0 flex-1 flex-col items-center justify-center px-6 text-center"
                    data-cy="dopamine-assistant-unavailable"
                  >
                    <DopamineAssistantMark className="mb-4 h-8 w-8" gradient />
                    <span className="abc-bold text-sm text-white/90">
                      {t('DOPAMINE_ASSISTANT_UNAVAILABLE')}
                    </span>
                    <span className="mt-2 text-xs leading-5 text-white/55">
                      {t('DOPAMINE_ASSISTANT_UNAVAILABLE_DESCRIPTION')}
                    </span>
                  </output>
                )}
              </div>
            </div>
          </div>
          {canResizePanel ? (
            <DopamineAssistantResizeHandle
              ariaControls={PANEL_ID}
              className="dopamine-panel-resizer absolute inset-y-0 right-0 z-20 w-1.5"
              dataCy="dopamine-assistant-panel-resizer"
              label={t('DOPAMINE_RESIZE_PANEL')}
              resize={panelResize}
            />
          ) : null}
        </div>
        <DopamineAssistantRail
          buttonRef={openButtonRef}
          hasUnreadOutcome={Boolean(unreadOutcome)}
          isExpanded={isExpanded}
          railStatus={railStatus}
          railStatusText={railStatusText}
          onOpen={() => updateExpandedState(true)}
        />
      </div>
    </aside>
  )
}
